import { getViemClients, } from "@bracket/contractor-next";
import type { Config, } from "@wagmi/core";


import {
	getContractAddress,
	getContractWithABI,
	getTokenDefinition,
	isAddress,
} from "@bracket/chainography";

import type {
	ContractAddress,
	TokenIdentifier,
} from "@bracket/types";


export declare type AllowanceProps = {
	spender: ContractAddress;
	token: TokenIdentifier;
	config?: Config;
};


/**
 * Gets the allowance amount for a spender to spend tokens on behalf of the wallet
 * @param {AllowanceProps} params - The parameters for checking allowance
 * @param {ContractAddress} params.spender - The address or contract name to check allowance for
 * @param {TokenIdentifier} params.token - The token to check allowance for
 * @param {Config} [params.config] - Optional wagmi config
 * @param {number} [params.chain=1] - The chain ID to operate on (currently only supports 1)
 * @returns {Promise<bigint>} The allowance amount
 * @throws {Error} If chain is not 1 or if contract read fails
 */
export const allowance = async ({
	token, 
	spender,
	config,
}: AllowanceProps) => {

	try {

		const { chainId, } = token;

		const [
			client,
			wallet,
		] = await getViemClients({ chainId, config, });

		const { address:walletAddress, } = wallet.account;
		const { address:tokenAddress, symbol } = getTokenDefinition(token);

		// ETH has an infinite allowance	
		if(symbol === "ETH" || symbol === "BTC") {
			return BigInt(2n ** 1000n);
		}

		const [_, abi] = getContractWithABI("ERC20", chainId);
		const spenderAddress = isAddress(spender) ? spender : getContractAddress(spender);
		const result = await client.readContract({
			address: tokenAddress,
			abi,
			functionName: "allowance",
			args: [
				walletAddress,
				spenderAddress,
			]
		})

		if(typeof result === "bigint") {
			return result;
		}

		throw new Error("unexpected result - expected bigint");

	} catch (err) {

		throw new Error(`@bracket/chainography/erc20:allowance ${err.message}`);
	}
};
